import { ExButton } from '@boomi/exosphere';
import { useCallback, useEffect, useState } from 'react';
import Table, { type TableColumnList } from '../Table';
import SearchInput from '../SearchInput';
import { getAllEnvironmentVariables } from '../../../sources/environments';
import type { OrderDirection } from '../../../types/api';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import type { EnvironmentVariable } from '../../../types/environment';

const PAGE_SIZE = 20;

type VaribleListProps = {
    onSelect: (variableName: string) => void;
};

const VariableList = ({ onSelect }: VaribleListProps) => {
    const dispatchNotification = useNotificationDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [variables, setVariables] = useState<EnvironmentVariable[]>([]);
    const [paging, setPaging] = useState({
        page: 1,
        pageSize: PAGE_SIZE,
        total: 0,
        searchTerm: '',
    });

    const loadEnvironmentVariables = useCallback(
        async ({ searchTerm, page }: { searchTerm: string; page: number }) => {
            try {
                const request = {
                    searchTerm,
                    page,
                    pageSize: PAGE_SIZE,
                    orderBy: 'name',
                    orderDirection: 'ASC' as OrderDirection,
                };

                return await getAllEnvironmentVariables(request);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [dispatchNotification],
    );

    const columns: TableColumnList<EnvironmentVariable> = [
        {
            renderHeader: () => 'Actions',
            renderCell: ({ item }) => (
                <div className="flex">
                    <ExButton data-testid={item.id} onClick={() => onSelect(item.name)}>
                        Select
                    </ExButton>
                </div>
            ),
            size: '82px',
        },
        {
            renderHeader: () => 'Name',
            renderCell: ({ item }) => item.name,
        },
    ];

    const onPage = async (pageNumber: number) => {
        setIsLoading(true);

        setPaging({
            ...paging,
            page: pageNumber,
        });

        const results = await loadEnvironmentVariables({
            searchTerm: paging.searchTerm,
            page: pageNumber,
        });

        if (results) {
            setVariables(results.items);
        }
    };

    const onSearch = async (query: string) => {
        setIsLoading(true);

        const results = await loadEnvironmentVariables({
            searchTerm: query,
            page: 1,
        });

        if (results) {
            setPaging({
                searchTerm: query,
                total: results._meta.total,
                pageSize: results._meta.pageSize,
                page: results._meta.page,
            });

            setVariables(results.items);
        }
    };

    useEffect(() => {
        const onLoad = async () => {
            const results = await loadEnvironmentVariables({ searchTerm: '', page: 1 });

            if (results) {
                setPaging({
                    searchTerm: '',
                    total: results._meta.total,
                    pageSize: results._meta.pageSize,
                    page: results._meta.page,
                });

                setVariables(results.items);
            }
        };

        onLoad();
    }, [loadEnvironmentVariables]);

    return (
        <>
            <div className="flex margin-bottom filterHeading padding">
                <SearchInput
                    className="tab-header-search inline-block"
                    value={paging.searchTerm}
                    onChange={onSearch}
                />
            </div>
            <Table
                columns={columns}
                items={variables}
                pagination={{
                    page: paging.page,
                    pageSize: paging.pageSize,
                    total: paging.total,
                    changePage: onPage,
                }}
                isLoading={isLoading}
            />
        </>
    );
};

export default VariableList;
